import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  ReactiveFormsModule,
  FormControl,
  Validators,
} from '@angular/forms';
import { RouterLink, Router, ActivatedRoute } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WorkflowService } from '../../../workflow.service';
import { AssigneeInputDependency } from '../../../workflow.model';
import { AddIconComponent, RemoveItemIconComponent } from '@kuiper/icons';
import { CustomizedDropdownComponent } from '../../../../../../../ui-components/lib/customized-dropdown/customized-dropdown.component';
import { PaginatedSelectComponent } from '../../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { CheckboxComponent } from '../../../../../../../ui-components/lib/checkbox/checkbox.component';
import { AvatarComponent } from '../../../../../../../ui-components/lib/avatar/avatar.component';
import { SelectComponent } from '../../../../../../../ui-components/lib/select/select.component';
import { ToasterService } from '../../../../../../../ui-components/services/toast/toast.service';
import { TruncatePipe } from '../../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { OptionItem } from '../../../../../../../ui-components/lib/option.interface';
import { OverlayLoaderComponent } from '../../../../../../common/components/overlay-loader/overlay-loader.component';
import { FeatureFlagsService } from '../../../../../../common/services/feature-flags/feature-flags.service';
import { TenantService } from '../../../../../../common/services/tenant/tenant.service';
import { LookupService } from '../../../../../../common/services/lookup/lookup.service';
import { TenantPathPipe } from '../../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { baseUrl } from '../../../../../../common/constants/endpoints.constants';
import { FEATURE_FLAGS } from '../../../../../../common/constants/feature-flags.constants';
import { AppPermissions } from '../../../../../../common/constants/app-permissions';

@Component({
  selector: 'lib-workflow-details-table-add-member',
  imports: [
    ReactiveFormsModule,
    CheckboxComponent,
    AvatarComponent,
    TruncatePipe,
    TranslocoPipe,
    RouterLink,
    OverlayLoaderComponent,
    SelectComponent,
    CustomizedDropdownComponent,
    PaginatedSelectComponent,
    RemoveItemIconComponent,
    AddIconComponent,
    TenantPathPipe,
  ],
  templateUrl: './workflow-details-table-add-member.component.html',
  styleUrl: './workflow-details-table-add-member.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowDetailsTableAddMemberComponent
  implements OnInit, OnDestroy
{
  fb = inject(FormBuilder);
  userService = inject(LookupService);
  workflowService = inject(WorkflowService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  toasterService = inject(ToasterService);
  transloco = inject(TranslocoService);
  tenantService = inject(TenantService);
  featureFlagsService = inject(FeatureFlagsService);

  readonly isScheduleWorkflowToRunOnceEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.schedule_a_workflow_to_run_once
    )
  );

  private readonly viewDependentPermissions = [
    'trigger',
    'view_all',
    'archive',
    'hold',
    ...AppPermissions.workflow.schedule.view,
    ...AppPermissions.workflow.schedule.add,
    ...AppPermissions.workflow.schedule.edit,
    ...AppPermissions.workflow.schedule.cancel,
  ];

  private readonly scheduleDependentPermissions = [
    ...AppPermissions.workflow.schedule.add,
    ...AppPermissions.workflow.schedule.edit,
    ...AppPermissions.workflow.schedule.cancel,
  ];

  page = signal<number>(1);
  totalPages = signal<number>(1);
  isUserLoading = signal<boolean>(false);

  mode = signal<string>('add');
  flowTemplateId = signal<string>('');
  flowTemplateCategoryId = input<number>(0);
  member = signal<any>({});
  memberId = signal<string>('');

  isLoading = signal<boolean>(true);
  isFormChanged = signal<boolean>(false);
  initialFormValues: any = {};

  availableUsers: any[] = [];
  selectedUsers: any[] = [];

  assigneeSelectDependency = signal<AssigneeInputDependency[]>([
    {
      api: '',
      selectedOption: { label: '', value: '' },
      placeholder: this.transloco.translate(
        'workflow_details_table_add_member_screen_select_resource_placeholder'
      ),
      filters: [],
      searchLabel: '',
      searchPlaceholder: '',
    },
  ]);
  destroy$ = new Subject<void>();

  assigneeTypeOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate(
        'workflow_details_table_add_member_screen_{department}_label'
      ),
      value: 'department',
    },
    {
      label: this.transloco.translate(
        'workflow_details_table_add_member_screen_{group}_label'
      ),
      value: 'group',
    },
    {
      label: this.transloco.translate(
        'workflow_details_table_add_member_screen_{legal_entity}_label'
      ),
      value: 'legal_entity',
    },
    {
      label: this.transloco.translate(
        'workflow_details_table_add_member_screen_{user}_label'
      ),
      value: 'user',
    },
  ]);

  subscription = new Subscription();

  constructor() {
    this.route.params.subscribe((params: any) => {
      this.flowTemplateId.set(params.permission_id);
      this.mode.set(params.mode);
      this.memberId.set(params.member_id);
    });
  }

  memberPermissionsForm = signal<FormGroup>(
    this.fb.group({
      members: this.fb.array([]),
    })
  );

  ngOnInit() {
    if (this.mode() === 'edit' && this.member()) {
      this.getMemberDetails();
    } else {
      this.addMember();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  createMemberFormGroup(): FormGroup {
    const group = this.fb.group({
      model_type: ['', Validators.required],
      model_id: ['', Validators.required],
      permissions: this.fb.group({
        view: [false],
        trigger: [false],
        view_all: [false],
        archive: [false],
        hold: [false],
        view_workflow_schedule: [false],
        add_workflow_schedule: [false],
        edit_workflow_schedule: [false],
        cancel_workflow_schedule: [false],
      }),
    });

    const permissions = group.get('permissions') as FormGroup;
    this.setupPermissionDependencies(permissions);

    permissions.setValidators(() => {
      const viewSelected = permissions.get('view')?.value;
      if (!viewSelected) {
        return { noViewPermissionSelected: true };
      }
      return null;
    });

    return group;
  }

  private setupPermissionDependencies(permissions: FormGroup): void {
    const view = permissions.get('view') as FormControl;
    const trigger = permissions.get('trigger') as FormControl;
    const viewWorkflowSchedule = permissions.get(
      'view_workflow_schedule'
    ) as FormControl;

    this.viewDependentPermissions.forEach(permissionKey => {
      const control = permissions.get(permissionKey) as FormControl;
      if (!control) {
        return;
      }

      this.subscription.add(
        control.valueChanges.subscribe(checked => {
          if (checked) {
            view.setValue(true);
            if (this.scheduleDependentPermissions.includes(permissionKey)) {
              viewWorkflowSchedule.setValue(true);
            }
            if (permissionKey === 'add_workflow_schedule') {
              trigger.setValue(true);
            }
            return;
          } else if (permissionKey === 'add_workflow_schedule') {
            trigger.setValue(false);
          }

          if (
            !this.isAnyPermissionChecked(
              permissions,
              this.viewDependentPermissions,
              permissionKey
            )
          ) {
            view.setValue(false);
          }

          if (
            permissionKey !== 'view_workflow_schedule' &&
            this.scheduleDependentPermissions.includes(permissionKey) &&
            !this.isAnyPermissionChecked(
              permissions,
              this.scheduleDependentPermissions,
              permissionKey
            )
          ) {
            viewWorkflowSchedule.setValue(false);
          }
        })
      );
    });
  }

  private isAnyPermissionChecked(
    permissions: FormGroup,
    permissionKeys: string[],
    excludePermission?: string
  ): boolean {
    return permissionKeys.some(
      key => key !== excludePermission && !!permissions.get(key)?.value
    );
  }

  isViewPermissionDisabled(index: number): boolean {
    return this.isAnyPermissionChecked(
      this.getMemberFormGroup(index),
      this.viewDependentPermissions
    );
  }

  isViewWorkflowScheduleDisabled(index: number): boolean {
    return this.isAnyPermissionChecked(
      this.getMemberFormGroup(index),
      this.scheduleDependentPermissions
    );
  }

  isTriggerPermissionDisabled(index: number): boolean {
    return !!this.getMemberFormGroup(index).get('add_workflow_schedule')?.value;
  }

  selectAssignableType(event: any, index: number) {
    const currentDependencies = [...this.assigneeSelectDependency()];
    const assignee = {
      type: event.value,
      data: { id: '', name: '' },
    };
    currentDependencies[index] = this.getAssigneeType(assignee);
    this.assigneeSelectDependency.set(currentDependencies);

    this.membersFormArray.controls[index]
      .get('model_type')
      ?.setValue(event.value, { emitEvent: false });
    this.membersFormArray.controls[index].get('model_id')?.enable();
    this.membersFormArray.controls[index].get('model_id')?.setValue('');
  }

  updateAssigneeDependencyOnValueChange(index: number) {
    this.membersFormArray.controls[index]
      .get('model_type')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!value) return;

        this.removeValidationsForUserType(value, index);

        const currentDependencies = [...this.assigneeSelectDependency()];
        const assignee = {
          type: value,
          data: { id: '', name: '' },
        };
        currentDependencies[index] = this.getAssigneeType(assignee);
        this.assigneeSelectDependency.set(currentDependencies);
      });
  }
  // REFACTOR --> DELETE THIS FUNCTION
  // this function removes the validations for assignee preference if user type is selected
  removeValidationsForUserType(type: string, index: number) {
    if (type === 'user') {
      const formGroup = this.membersFormArray.controls[index] as FormGroup;
      const preferenceController = formGroup?.controls['preference'];
      const assignmentPreferenceController =
        formGroup?.controls['assignment_preference'];

      preferenceController?.setValue(null);
      preferenceController?.removeValidators(Validators.required);
      preferenceController?.updateValueAndValidity();

      assignmentPreferenceController?.removeValidators(Validators.required);
      assignmentPreferenceController?.updateValueAndValidity();
    }
  }
  // REFACTOR
  getAssigneeType(assignee: any): AssigneeInputDependency {
    const baseApiUrl = `${baseUrl}/lookup`;
    const defaultFilters = [{ key: 'search', value: '' }];
    const activeStateFilter = [{ key: 'state', value: 'active' }];
    const notInactiveStateFilter = [
      { key: 'not_states][0', value: 'inactive' },
    ];
    const notCancelledStateFilter = [
      { key: 'not_states][1', value: 'cancelled' },
    ];
    const filtersNotPermissionForResourcesResourceType0 = [
      {
        key: 'not_permissions_for_resources][0][resource_type',
        value: 'flow_template',
      },
      {
        key: 'not_permissions_for_resources][0][resource_id',
        value: this.flowTemplateId(),
      },
    ];

    const filtersNotPermissionForResourcesResourceType1 = [
      {
        key: 'not_permissions_for_resources][1][resource_type',
        value: 'flow_template_category',
      },
      {
        key: 'not_permissions_for_resources][1][resource_id',
        value: this.flowTemplateCategoryId(),
      },
    ];

    const defaultSelectedOption = {
      label: assignee.data?.name ?? '',
      value: assignee.data?.id ?? '',
    };

    switch (assignee.type) {
      case 'department':
        return {
          api: `${baseApiUrl}/departments`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_configurations_details_screen_select_department_placeholder'
          ),
          filters: [
            ...defaultFilters,
            ...activeStateFilter,
            ...filtersNotPermissionForResourcesResourceType0,
            ...filtersNotPermissionForResourcesResourceType1,
          ],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_department_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      case 'group':
        return {
          api: `${baseApiUrl}/groups`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_configurations_details_screen_select_group_placeholder'
          ),
          filters: [
            ...defaultFilters,
            ...activeStateFilter,
            ...filtersNotPermissionForResourcesResourceType0,
            ...filtersNotPermissionForResourcesResourceType1,
          ],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_group_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      case 'legal_entity':
        return {
          api: `${baseApiUrl}/legal-entities`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_step_info_form_{legal_entity}_label'
          ),
          filters: [
            ...defaultFilters,
            ...activeStateFilter,
            ...filtersNotPermissionForResourcesResourceType0,
            ...filtersNotPermissionForResourcesResourceType1,
          ],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_legal_entity_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      default:
        return {
          api: `${baseApiUrl}/users`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_configurations_details_screen_select_user_placeholder'
          ),
          filters: [
            ...defaultFilters,
            ...notInactiveStateFilter,
            ...notCancelledStateFilter,
            ...filtersNotPermissionForResourcesResourceType0,
            ...filtersNotPermissionForResourcesResourceType1,
          ],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_user_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
    }
  }

  handlePermissions(permission: string, permissionArray: any[]) {
    if (!permissionArray) {
      return;
    }

    const mappedArray = permissionArray.map(item => item.action);
    return mappedArray.includes(permission);
  }

  checkForChanges() {
    const currentValues = this.memberPermissionsForm().getRawValue();
    const hasChanges =
      JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues);
    this.isFormChanged.set(hasChanges);
  }

  setupChangeDetection() {
    this.membersFormArray.valueChanges.subscribe(() => {
      this.checkForChanges();
    });
  }

  get membersFormArray() {
    return this.memberPermissionsForm().get('members') as FormArray;
  }

  selectResource(event: any, index: number) {
    const formGroup = this.membersFormArray.controls[index] as FormGroup;

    const resourceType = formGroup.get('model_type')?.value;
    formGroup.get('model_id')?.setValue(event.value || event.id);

    this.selectedUsers[index] = {
      label: event.label || event.name,
      value: event.value || event.id,
      role: this.getResourceTypeLabel(resourceType),
      image: event.image,
    };
  }

  getResourceTypeLabel(type: string): string {
    switch (type) {
      case 'department':
        return 'Department';
      case 'group':
        return 'Group';
      case 'legal_entity':
        return 'Legal Entity';
      case 'user':
        return 'User';
      default:
        return 'Resource';
    }
  }

  addMember() {
    this.isLoading.set(false);
    const newFormGroup = this.createMemberFormGroup();
    this.membersFormArray.push(newFormGroup);

    // Create a proper default dependency with placeholder
    const defaultDependency: AssigneeInputDependency = {
      api: '',
      selectedOption: { label: '', value: '' },
      placeholder: this.transloco.translate(
        'workflow_details_table_add_member_screen_select_resource_placeholder'
      ),
      filters: [],
      searchLabel: '',
      searchPlaceholder: '',
    };

    const currentDependencies = [...this.assigneeSelectDependency()];
    currentDependencies.push(defaultDependency);
    this.assigneeSelectDependency.set(currentDependencies);

    this.updateAssigneeDependencyOnValueChange(
      this.membersFormArray.length - 1
    );

    this.selectedUsers.push(null);
  }

  save() {
    if (this.memberPermissionsForm().invalid) {
      this.memberPermissionsForm().markAllAsTouched();

      const membersArray = this.membersFormArray;
      for (let i = 0; i < membersArray.length; i++) {
        const permissions = membersArray.at(i).get('permissions');
        if (permissions?.errors?.['noViewPermissionSelected']) {
          this.toasterService.add(
            this.transloco.translate(
              'workflow_details_table_add_member_no_view_permission_selected'
            ),
            '',
            '',
            'error',
            true,
            true
          );
          return;
        }
      }
    } else {
      this.isLoading.set(true);
      if (this.mode() === 'add') {
        this.addWorkflowMembers();
      } else {
        this.updateWorkflowMember();
      }
    }
  }

  addWorkflowMembers() {
    this.workflowService
      .addWorkflowMember(
        this.flowTemplateId(),
        this.memberPermissionsForm().getRawValue()
      )
      .subscribe({
        next: () => this.handleSaveSuccess(),
        error: (error: any) => this.handleSaveError(error),
      });
  }

  updateWorkflowMember() {
    const permissions = this.membersFormArray.at(0).get('permissions')?.value;

    this.workflowService
      .updateWorkflowMember(this.flowTemplateId(), this.member().id, {
        permissions,
      })
      .subscribe({
        next: () => this.handleSaveSuccess(),
        error: (error: any) => this.handleSaveError(error),
      });
  }

  handleSaveSuccess() {
    this.isLoading.set(false);
    this.router.navigate([
      this.tenantService.adminPath('workflow/permissions/permission-details/'),
      this.flowTemplateId(),
      'view',
    ]);
  }

  handleSaveError(error: any) {
    this.isLoading.set(false);
    this.toasterService.add(error.message, '', '', 'error', true, true);
  }

  getMemberDetails() {
    this.workflowService
      .getWorkflowMemberDetails(this.flowTemplateId(), this.memberId())
      .subscribe({
        next: (res: any) => this.handleMemberDetailsSuccess(res),
        error: () => {
          this.isLoading.set(false);
          this.router.navigate([
            this.tenantService.adminPath(
              'workflow/permissions/permission-details/'
            ),
            this.flowTemplateId(),
            'view',
          ]);
        },
      });
  }

  handleMemberDetailsSuccess(res: any) {
    this.member.set(res.data);
    this.patchMemberPermissions();
    this.isLoading.set(false);
  }

  patchMemberPermissions() {
    const memberGroup = this.createMemberFormGroup();
    if (!this.member()?.permissions) {
      return;
    }

    const permissions = this.member().permissions;

    const permissionValues: Record<string, boolean | undefined> = {
      view: this.handlePermissions('view', permissions),
      trigger: this.handlePermissions('trigger', permissions),
      view_all: this.handlePermissions('view_all', permissions),
      archive: this.handlePermissions('archive', permissions),
      hold: this.handlePermissions('hold', permissions),
      view_workflow_schedule: this.handlePermissions(
        'view_workflow_schedule',
        permissions
      ),
      add_workflow_schedule: this.handlePermissions(
        'add_workflow_schedule',
        permissions
      ),
      edit_workflow_schedule: this.handlePermissions(
        'edit_workflow_schedule',
        permissions
      ),
      cancel_workflow_schedule: this.handlePermissions(
        'cancel_workflow_schedule',
        permissions
      ),
    };

    memberGroup.patchValue(
      {
        model_id: this.member().id,
        model_type: this.member().model_type,
        permissions: permissionValues,
      },
      { emitEvent: false }
    );

    this.membersFormArray.push(memberGroup);

    this.initialFormValues = this.memberPermissionsForm().getRawValue();
    this.setupChangeDetection();

    this.selectedUsers[0] = {
      label: this.member().model_data.name,
      image: this.member().model_data.image,
    };
  }

  removeResource(index: number) {
    const indexToRemove = index;
    this.membersFormArray.removeAt(indexToRemove);

    const currentDependencies = [...this.assigneeSelectDependency()];
    if (indexToRemove < currentDependencies.length) {
      currentDependencies.splice(indexToRemove, 1);
      this.assigneeSelectDependency.set(currentDependencies);
    }

    if (indexToRemove < this.selectedUsers.length) {
      this.selectedUsers.splice(indexToRemove, 1);
    }
  }

  getMemberControl(index: number, controlName: string): FormControl {
    const memberGroup = this.membersFormArray.at(index) as FormGroup;
    if (controlName.includes('permissions.')) {
      const [group, field] = controlName.split('.');
      return memberGroup.get(group)?.get(field) as FormControl;
    }
    return memberGroup.get(controlName) as FormControl;
  }

  getMemberFormGroup(index: number): FormGroup {
    const memberGroup = this.membersFormArray.at(index) as FormGroup;
    return memberGroup.get('permissions') as FormGroup;
  }
}
